import { Component, OnDestroy, OnInit } from '@angular/core';
import moment from 'moment';
moment.tz.setDefault('Europe/Berlin');

import { BasePage, MinutesToHoursPipe } from 'pbc.angular';
import { ZeitenMitarbeiterService } from '../../querys/mitarbeiter/mitarbeiter.service';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { IAktivitaetDto, IArbeitszeitkonto, IKrankheitDto, ITag, ITagDto, IUrlaubDto, IUrlaubsKonto } from 'fa-kt.types';
import { ProjekteService } from '../../../projekte';
import { MitarbeiterService } from '../../../personen';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'fa-kt-zeiten-mitarbeiter-page',
    templateUrl: './mitarbeiter.page.html',
    styleUrls: ['./mitarbeiter.page.css']
})
export class MitarbeiterPage extends BasePage implements OnInit, OnDestroy {
    override description = { context: 'ZEITEN', page: 'MITARBEITER' };

    private readonly subscriptions: Subscription[] = [];

    selectedMitarbeiter: string = ''
    mitarbeiter: object[] = []
    isLoadingMitarbeiter = true
    $aktuellerMitarbeiter = new BehaviorSubject<string>(null)
    $zeitraum = new BehaviorSubject<{ von: Date, bis: Date }>({
        von: moment().startOf('month').toDate(),
        bis: moment().subtract(2, 'day').endOf('day').toDate()
    })
    aktuellerZeitraum: 'aktueller-monat' | 'letzter-monat' | 'dieses-jahr' | 'custom-zeitraum' = 'aktueller-monat'
    customZeitraum: Date[]
    isVisibleSelectCustomZeitraum: boolean = false
    jahr: number

    $tage = new BehaviorSubject<{ [key: string]: ITagDto } | undefined>(undefined)
    $arbeitszeitKonto = new BehaviorSubject<IArbeitszeitkonto | undefined>(undefined)
    $arbeitszeitKontoAktuellesJahr = new BehaviorSubject<IArbeitszeitkonto | undefined>(undefined)
    $urlaubsKonto = new BehaviorSubject<IUrlaubsKonto | undefined>(undefined)
    $urlaube = new BehaviorSubject<IUrlaubDto[] | undefined>(undefined)
    $krankheitstage = new BehaviorSubject<IKrankheitDto[] | undefined>(undefined)
    zeiten: object = {}
    zeitenSortiert: string[] = []
    tage: Object = {}
    tageSortiert: string[] = []
    tageNichtZugeordnet: Object[] = []
    zeitArten: Object = {}
    zeitUnterarten: Object = {}
    projekte: Object = {}
    calenderDate = new Date()
    calenderEvents: Array<{ date: Date, type: 'arbeitstag' | 'urlaub' | 'feiertag' | 'krankheit', anzahlMinuten?: number, name?: string }> = []
    view: 'kalender' | 'kacheln' = 'kacheln'

    constructor(
        public zeitenMitarbeiter: ZeitenMitarbeiterService,
        public projekteService: ProjekteService,
        public mitarbeiterService: MitarbeiterService,
        public datePipe: DatePipe,
        public minutesToHourPipe: MinutesToHoursPipe
    ) {
        super();
    }

    override async ngOnInit() {
        await super.ngOnInit();
        this.subscriptions.push(
            this.auth.$id.subscribe(async (id) => {
                if (!id) {
                    return
                }

                this.mitarbeiterService.request({}).then((response) => {
                    this.mitarbeiter = response.mitarbeiter
                        .filter(x => !x.mitarbeiter.inaktiv)
                        .map((x) => ({
                            id: x.mitarbeiter.id,
                            vorname: x.mitarbeiter.vorname,
                            nachname: x.mitarbeiter.nachname,
                            email: x.mitarbeiter.email,
                            anzeigename: x.mitarbeiter.anzeigename
                        }))
                        .sort((a, b) => {
                            const nameA = a.anzeigename.toLowerCase()
                            const nameB = b.anzeigename.toLowerCase()
                            return nameA.localeCompare(nameB)
                        })
                    this.isLoadingMitarbeiter = false
                })

                this.selectedMitarbeiter = id
                this.$aktuellerMitarbeiter.next(this.selectedMitarbeiter)
            }),
            combineLatest([this.$aktuellerMitarbeiter, this.$zeitraum])
                .subscribe(([mitarbeiterId, zeitraum]) => {
                    if (!mitarbeiterId) {
                        this.reset()
                        return
                    }
                    this.loadData(mitarbeiterId, zeitraum.von, zeitraum.bis)
                }),
            this.$zeitraum.subscribe(zeitraum => {
                this.customZeitraum = [zeitraum.von, zeitraum.bis]
            })
        )
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.subscriptions.forEach(($) => $.unsubscribe());
    }

    async loadData(mitarbeiter: string, von: Date, bis: Date) {
        this.loading$.next(true)
        try {
            const response = await this.zeitenMitarbeiter.request({ von, bis, mitarbeiter })
            const {
                zeitArten, zeitUnterarten,
                feiertage, jahr,
                zeiten, tage, tageNichtZugeordnet,
                arbeitszeitKonto, arbeitszeitKontoAktuellesJahr,
                urlaubsKonto, urlaube,
                krankheitstage
            } = response

            this.zeitArten = zeitArten.reduce((group, item) => {
                if (!item.id) {
                    throw new Error('ID is missing')
                }
                if (group[item.id]) {
                    throw new Error(`Multiple IDs: ${item.id}`)
                }
                group[item.id] = item
                return group
            }, {} as { [key: string]: any })
            this.zeitArten['pause'] = { name: 'Pause' }
            this.zeitUnterarten = zeitUnterarten.reduce((group, item) => {
                if (!item.id) {
                    throw new Error('ID is missing')
                }
                if (group[item.id]) {
                    throw new Error(`Multiple IDs: ${item.id}`)
                }
                group[item.id] = item
                return group
            }, {} as { [key: string]: any })
            this.jahr = jahr

            const tageGroupedByDate = tage.reduce((group, item) => {
                const date = moment(item.start).format('YYYY-MM-DD');
                if (group[date]) {
                    throw new Error(`Multiple Tage for Date: ${date}`)
                }
                group[date] = item
                return group
            }, {} as { [key: string]: ITagDto })
            this.$tage.next(tageGroupedByDate)
            this.tage = tageGroupedByDate
            this.tageSortiert = Object.keys(this.tage).sort((a, b) => moment(b).toDate().getTime() - moment(a).toDate().getTime())
            this.tageNichtZugeordnet = tageNichtZugeordnet.filter(x => x.minuten.arbeit != 0)

            this.zeiten = zeiten.reduce((zeiten, current) => {
                const matchingTag = this.tage[moment(current.datum).format('YYYY-MM-DD')]
                let recordsMitPausen
                if (matchingTag != null) {
                    recordsMitPausen = [
                        ...current.records,
                        ...matchingTag.pausen.map(x => {
                            const anzahlMinuten = moment.utc(x.ende).diff(moment.utc(x.start), 'minutes')
                            const stunden = Math.floor(anzahlMinuten / 60)
                            const minuten = anzahlMinuten % 60
                            return { ...x, zeitArt: 'pause', stunden, minuten }
                        })
                    ]
                    recordsMitPausen.sort((a, b) => moment(a.start).toDate().getTime() - moment(b.start).toDate().getTime())
                } else {
                    recordsMitPausen = current.records
                }
                zeiten[current.datum] = {
                    ...current,
                    records: recordsMitPausen
                }
                return zeiten
            }, {} as { [key: string]: IAktivitaetDto })
            this.zeitenSortiert = Object.keys(this.zeiten).sort((a, b) => moment(b).toDate().getTime() - moment(a).toDate().getTime())

            this.$arbeitszeitKonto.next(arbeitszeitKonto)
            this.$arbeitszeitKontoAktuellesJahr.next(arbeitszeitKontoAktuellesJahr)
            this.$urlaubsKonto.next(urlaubsKonto)
            this.$urlaube.next([...urlaube].sort((a, b) => moment(a['von']).toDate().getTime() - moment(b['von']).toDate().getTime()))
            this.$krankheitstage.next(krankheitstage)

            feiertage.forEach(feiertag => {
                const date = moment(feiertag.date)
                if (this.calenderEvents.find(x => moment(x.date).isSame(date) && x.type == 'feiertag') == null) {
                    this.calenderEvents.push({ date: date.toDate(), type: 'feiertag', name: feiertag.name })
                }
            })
            Object.entries(this.zeiten).forEach(([datum, zeit]) => {
                const date = moment(datum)
                if (this.calenderEvents.find(x => moment(x.date).isSame(date) && x.type == 'arbeitstag') == null) {
                    this.calenderEvents.push({ date: date.toDate(), type: 'arbeitstag', anzahlMinuten: zeit.anzahlMinuten })
                }
            })
            urlaube.forEach(urlaub => {
                urlaub['tage'].forEach(tag => {
                    const date = moment(tag)
                    if (this.calenderEvents.find(x => moment(x.date).isSame(date) && x.type == 'urlaub') == null) {
                        this.calenderEvents.push({ date: date.toDate(), type: 'urlaub' })
                    }
                })
            })
            krankheitstage.forEach(krankheitstag => {
                krankheitstag['tage'].forEach(tag => {
                    const date = moment(tag)
                    if (this.calenderEvents.find(x => moment(x.date).isSame(date) && x.type == 'krankheit') == null) {
                        this.calenderEvents.push({ date: date.toDate(), type: 'krankheit' })
                    }
                })
            })
            this.loading$.next(false)
        } catch (e) {
            this.loading$.next(false)
            this.message.error(e.error.message)
        }
    }

    reset() {
        this.$tage.next(undefined)
        this.$arbeitszeitKonto.next(undefined)
        this.$urlaubsKonto.next(undefined)
        this.$urlaube.next(undefined)
        this.$krankheitstage.next(undefined)
        this.zeiten = {}
        this.zeitenSortiert = []
        this.tage = {}
        this.zeitArten = {}
        this.zeitUnterarten = {}
        this.projekte = {}
        this.calenderEvents = []
    }

    back() {
        window.history.back();
    }

    onSwitchView(view) {
        this.view = view
    }

    onSelectMitarbeiter() {
        this.reset()
        this.$aktuellerMitarbeiter.next(this.selectedMitarbeiter)
    }

    onPanelDayActivitiesChange(day: any, isActive: boolean): void {
        if (!isActive) {
            return
        }
        [...new Set(this.zeiten[day].records
            .filter(x => x.projekt != null)
            .map(x => x.projekt))]
            .filter((x: string) => !(x in this.projekte))
            .forEach((x: string) => {
                this.projekteService.request({ projekt: x })
                    .then((response) => {
                        this.projekte[x] = {
                            kunde: {
                                name: response.projekte[0].kunde.name
                            },
                            projekt: {
                                nummer: response.projekte[0].projekt.nummer,
                                bezeichnung: response.projekte[0].projekt.bezeichnung
                            }
                        }
                    })
            })
    }

    onClickZeigeAktuellerMonat() {
        this.aktuellerZeitraum = 'aktueller-monat'
        this.$zeitraum.next({
            von: moment().startOf('month').toDate(),
            bis: moment().subtract(2, 'day').endOf('day').toDate()
        })
    }

    onClickZeigeLetzerMonat() {
        this.aktuellerZeitraum = 'letzter-monat'
        this.$zeitraum.next({
            von: moment().startOf('month').subtract(1, 'month').toDate(),
            bis: moment().endOf('month').subtract(1, 'month').toDate()
        })
    }

    onClickZeigeDiesesJahr() {
        this.aktuellerZeitraum = 'dieses-jahr'
        this.$zeitraum.next({
            von: moment().startOf('year').toDate(),
            bis: moment().subtract(2, 'day').endOf('day').toDate()
        })
    }

    onClickCustomZeitraum() {
        this.aktuellerZeitraum = 'custom-zeitraum'
        this.isVisibleSelectCustomZeitraum = true
    }

    onChangeCustomZeitraum(result: Date[]): void {
        if (result.length == 0) {
            this.isVisibleSelectCustomZeitraum = false
            this.onClickZeigeAktuellerMonat()
            return
        }
        this.$zeitraum.next({
            von: moment(result[0]).toDate(),
            bis: moment(result[1]).startOf('day').toDate()
        })
    }

    calenderPanelChange(change: { date: Date; mode: string }): void {
        if (change.mode == 'month') {
            this.calenderSelectChange(change.date)
        }
    }

    calenderSelectChange(date: Date) {
        if (moment(date).isBetween(moment(this.$zeitraum.value.von), moment(this.$zeitraum.value.bis))) {
            return
        }
        this.$zeitraum.next({
            von: moment(date).startOf('month').toDate(),
            bis: moment(date).endOf('month').toDate()
        })
    }

    calenderGetEvents(date: Date) {
        return this.calenderEvents.filter(
            e => e.date.toDateString() === date.toDateString()
        );
    }

    calenderDisabledDate(current: Date) {
        const minYear = 2023
        const maxYear = new Date().getFullYear()
        const year = current.getFullYear()
        return year < minYear || year > maxYear
    }

    /*
    getMonthName(index: number): string {
        const date = new Date()
        date.setMonth(index)
        return new Intl.DateTimeFormat('de-DE', {
            month: 'long'
        }).format(date);
    }
    */

    formatDateAsTime(date: Date) {
        return date ? this.datePipe.transform(date, 'HH:mm') : '--:--'
    }

    formatDateRange(start: Date | string, end: Date | string): string {
        const from = this.datePipe.transform(start, 'dd.MM.yyyy')
        const to = this.datePipe.transform(end, 'dd.MM.yyyy')
        return `${from} - ${to}`
    }

    formatNichtZugeordneterTagDetails(tag: ITag): string {
        const dayLabel = tag.start ? this.datePipe.transform(tag.start, 'EEEE') : ''
        const dateLabel = tag.start ? this.datePipe.transform(tag.start, 'dd.MM.yyyy') : ''
        const start = this.formatDateAsTime(tag?.start)
        const arbeit = tag?.minuten?.arbeit != null ? this.minutesToHourPipe.transform(tag.minuten.arbeit) : '0h'
        const pause = tag?.minuten?.pause != null ? this.minutesToHourPipe.transform(tag.minuten.pause) : '0h'

        return `${dayLabel}, ${dateLabel} ${start} (${arbeit} Arbeit, ${pause} Pause)`
    }

    formatDayDetails(day: string): string {
        const tag = this.tage[day]
        const zeiten = this.zeiten[day]

        const dayLabel = day ? this.datePipe.transform(day, 'EEEE') : ''
        const dateLabel = day ? this.datePipe.transform(day, 'dd.MM.yyyy') : ''
        const start = this.formatDateAsTime(tag?.start)
        const ende = this.formatDateAsTime(tag?.ende)
        const arbeit = zeiten?.anzahlMinuten != null ? this.minutesToHourPipe.transform(zeiten.anzahlMinuten) : '0h'
        const pause = tag?.minuten?.pause != null ? this.minutesToHourPipe.transform(tag.minuten.pause) : '0h'

        return `${dayLabel}, ${dateLabel} ${start} - ${ende} (${arbeit} Arbeit, ${pause} Pause)`
    }

    getProjektLabel(projektKey: string): string {
        const projektData = this.projekte[projektKey]
        if (!projektData || !projektData.projekt || !projektData.kunde) {
            return ''
        }

        const bezeichnung = projektData.projekt.bezeichnung ?? ''
        const kundeName = projektData.kunde.name ?? ''
        const nummer = projektData.projekt.nummer ?? ''

        return `${bezeichnung} (${kundeName} / ${nummer})`
    }

    getColorArbeitszeitKontoArbeitszeit(minuten: number) {
        if (minuten < -20) {
            return 'red'
        }
        if (minuten < 0) {
            return 'orange'
        }
        return 'green'
    }

    getUrlaubsKontoInfo(data: IUrlaubsKonto): string {
        if (!data || !data['übertrag']) {
            return ''
        }

        const uebertrag = data['übertrag']
        const bis = uebertrag.bis
        const erlaubt = uebertrag.erlaubt ?? 0
        const genommen = uebertrag.genommen ?? 0
        const verfuegbar = uebertrag.verfügbar ?? 0

        const today = moment()

        let label = `genommen: ${genommen}\u00A0Tage`
        if (verfuegbar > 0 && today.isAfter(moment(bis)) && today.isBefore(today.clone().endOf('year'))) {
            label = `${label}, verfallen: ${verfuegbar}\u00A0Tage`
        }

        return `${erlaubt}\u00A0Tage (${label})`
    }
}
